import React, { useCallback, useEffect, useMemo, useState } from "react";
import CalendarStatsCards from "./models/calendars_model/CalendarStatsCards";
import CalendarToolbar from "./models/calendars_model/CalendarToolbar";
import CalendarView from "./models/calendars_model/CalendarView";
import UpcomingDeadlines from "./models/calendars_model/UpcomingDeadlines";
import { caApi } from "../../../backend_urls/caApi";

const getResponseData = (response) => {
    if (response?.data !== undefined) { return response.data; }
    return response;
};

const normalizeCalendarData = (data) => {
    if (!data || typeof data !== "object" || Array.isArray(data)) {
        return [];
    }

    return Object.entries(data).flatMap(
        ([date, tasks = []]) =>
            tasks.map((task) => ({
                ...task,
                calendar_date: date,
                employees: Array.isArray(task.employees)
                    ? task.employees
                    : [],
            }))
    );
};

const CATeamsCalendar = () => {
    const [calendarItems, setCalendarItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    const [filters, setFilters] = useState({
        search: "",
        employee: "",
        department: "",
        status: "",
    });

    const loadCalendar = useCallback(async (isRefresh = false) => {
        try {
            if (isRefresh) { setRefreshing(true);
            } else { setLoading(true); }

            setError("");

            const response = await caApi.getTeamCalendar();
            const data = getResponseData(response.data);

            setCalendarItems( normalizeCalendarData(data) );
        } catch (err) {
            console.error("Failed to load team calendar:", err);

            setError(
                err?.response?.data?.detail ||
                "Unable to load team calendar."
            );

            setCalendarItems([]);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => { loadCalendar(); }, [loadCalendar]);

    const updateFilter = useCallback((key, value) => {
        setFilters((previous) => ({
            ...previous,
            [key]: value,
        }));
    }, []);

    const resetFilters = useCallback(() => {
        setFilters({
            search: "",
            employee: "",
            department: "",
            status: "",
        });
    }, []);

    const employees = useMemo(() => {
        const names = new Set();

        calendarItems.forEach((item) => {
            item.employees.forEach((employee) => {
                if (employee) { names.add(employee); }
            });
        });

        return Array.from(names).sort();
    }, [calendarItems]);

    const filteredItems = useMemo(() => {
        const search = filters.search.trim().toLowerCase();

        return calendarItems.filter((item) => {
            const matchesSearch =
                !search ||
                [
                    item.title,
                    item.business_name,
                    item.business,
                    item.priority,
                    item.status,
                    ...item.employees,
                ]
                    .filter(Boolean)
                    .some((value) =>
                        String(value)
                            .toLowerCase()
                            .includes(search)
                    );

            const matchesEmployee =
                !filters.employee ||
                item.employees.includes(filters.employee);

            const matchesStatus =
                !filters.status ||
                item.status === filters.status;

            return (
                matchesSearch &&
                matchesEmployee &&
                matchesStatus
            );
        });
    }, [calendarItems, filters]);

    const stats = useMemo(() => {
        const now = new Date();

        const startOfToday = new Date(now);
        startOfToday.setHours(0, 0, 0, 0);

        const endOfToday = new Date(now);
        endOfToday.setHours(23, 59, 59, 999);

        const startOfWeek = new Date(now);
        const day = startOfWeek.getDay();

        const mondayOffset =
            day === 0 ? -6 : 1 - day;

        startOfWeek.setDate(
            startOfWeek.getDate() + mondayOffset
        );
        startOfWeek.setHours(0, 0, 0, 0);

        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(
            endOfWeek.getDate() + 6
        );
        endOfWeek.setHours(23, 59, 59, 999);

        const getDueDate = (item) => {
            const value =
                item.due_date ||
                item.calendar_date;

            const date = new Date(value);

            return Number.isNaN(date.getTime())
                ? null
                : date;
        };

        const todayTasks = calendarItems.filter((item) => {
            const date = getDueDate(item);

            return (
                date &&
                date >= startOfToday &&
                date <= endOfToday
            );
        }).length;

        const weekTasks = calendarItems.filter((item) => {
            const date = getDueDate(item);

            return (
                date &&
                date >= startOfWeek &&
                date <= endOfWeek
            );
        }).length;

        const overdueTasks = calendarItems.filter((item) => {
            const date = getDueDate(item);

            return (
                date &&
                date < startOfToday &&
                item.status !== "completed" &&
                item.status !== "cancelled"
            );
        }).length;

        return {
            todayTasks,
            weekTasks,
            overdueTasks,
        };
    }, [calendarItems]);

    return (
        <div>
            <CalendarStatsCards stats={stats} />

            <CalendarToolbar
                filters={filters}
                employees={employees}
                onSearchChange = { (value) => updateFilter("search", value) }
                onEmployeeChange = { (value) => updateFilter("employee", value) }
                onDepartmentChange = { (value) => updateFilter("department", value) }
                onStatusChange = { (value) => updateFilter("status", value) }
                onReset={resetFilters}
                taskCount={filteredItems.length}
                totalCount={calendarItems.length}
                loading={loading}
                refreshing={refreshing}
                onRefresh={() => loadCalendar(true)}
            />

            {error && (
                <div
                    style={{
                        marginBottom: "16px",
                        padding: "12px 16px",
                        borderRadius: "10px",
                        background: "#fef2f2",
                        color: "#b91c1c",
                        border: "1px solid #fecaca",
                    }}
                >
                    {error}
                </div>
            )}

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 2fr) minmax(280px, 1fr)",
                    gap: "24px",
                }}
            >
                <CalendarView
                    items={filteredItems}
                    loading={loading}
                />

                <UpcomingDeadlines
                    items={filteredItems}
                    loading={loading}
                />
            </div>
        </div>
    );
};

export default CATeamsCalendar;
